import { convertStripeAmount } from "@/utils/currency";
import { Bookmark, Check, Heart, X } from "@phosphor-icons/react/dist/ssr";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useState } from "react";
import PlanCard from "./PlanCard";

const SHORT_LABELS = {
  "Free": "Free",
  "Content Access": "All-Access",
  "Startup Registration": "Startup",
  "VC Registration": "Investor",
};

const BenefitValue = ({ allowed }) => {
  if (allowed === "Unlimited") {
    return (
      <>
        <Check className="text-green-500 -translate-y-px size-4 shrink-0" weight="bold" />
        <span className="text-xs text-green-500">Unlimited</span>
      </>
    );
  }
  if (allowed === true) {
    return <Check className="text-green-500 size-4 shrink-0" weight="bold" />;
  }
  if (allowed === false) {
    return <X className="text-red-500 size-4 shrink-0" weight="bold" />;
  }
  return <span className="text-xs text-orange-500">{allowed}</span>;
};

const MobilePricingComparison = ({
  planConfigs,
  planSubscription,
  currencyCode,
  priceData,
  priceDataMonthly,
  billingCycle,
  setBillingCycle,
  _priceData,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [investorValue, setInvestorValue] = useState("vc");

  const changePlan = (newIndex) => {
    if (newIndex === activeIndex) return;
    setVisible(false);
    setTimeout(() => {
      setActiveIndex(newIndex);
      setVisible(true);
    }, 150);
  };

  const { plan: currentPlan, benefits: currentBenefits, description: currentDescription } =
    planConfigs[activeIndex];

  const allCriteria = planConfigs[0].benefits;

  const getPrice = (plan) => {
    if (plan.plan_id === "Free") return 0;
    const priceObj =
      plan.plan_id === "Content Access" && billingCycle === "monthly"
        ? priceDataMonthly?.[plan.plan_id]
        : priceData[plan.plan_id];
    if (!priceObj) return 0;
    return Number(
      convertStripeAmount(priceObj.unit_amount, currencyCode).toFixed(2),
    ).toLocaleString();
  };

  const getPricePeriod = (planId) => {
    if (planId === "Free") return null;
    if (planId === "Content Access") return billingCycle === "monthly" ? "month" : "year";
    return "year";
  };

  const isVCActive = currentPlan.plan_id === "VC Registration";
  const isContentActive = currentPlan.plan_id === "Content Access";

  const fadeClass = `transition-opacity duration-150 ${visible ? "opacity-100" : "opacity-0"}`;

  return (
    <div>
      {/* Sticky plan tab pills — top-16 clears the mobile navbar (h-16) */}
      <div className="sticky top-16 z-30 bg-background pt-3 pb-2">
        <div className="container flex gap-2 overflow-x-auto no-scrollbar">
          {planConfigs.map(({ plan }, i) => (
            <button
              key={i}
              onClick={() => changePlan(i)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-all duration-200 ${
                i === activeIndex
                  ? "bg-white text-background"
                  : "bg-white/10 text-white/60"
              }`}
            >
              {SHORT_LABELS[plan.plan_id] ?? plan.name}
            </button>
          ))}
        </div>
      </div>

      {/* Card */}
      <div className="container pb-4 mt-3">
        <div className="bg-white text-background rounded-xl p-4">
          {/* Fading plan header */}
          <div className={fadeClass}>
            <p className="text-xl font-semibold tracking-tight">{currentPlan.name}</p>
            <p className="mt-1 text-xs leading-snug opacity-40">{currentDescription}</p>

            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center space-x-2.5">
                <p className="text-4xl font-bold leading-none tracking-tighter">
                  {getPrice(currentPlan)}
                </p>
                <div className="flex flex-col space-y-1.5">
                  <p className="text-sm font-semibold leading-none">
                    {currencyCode === "USD" ? "USD" : currencyCode}
                  </p>
                  {currentPlan.plan_id === "Free" && (
                    <p className="text-xs font-medium leading-none text-background/40">
                      Free Forever!
                    </p>
                  )}
                  {currentPlan.plan_id !== "Free" && (
                    <p className="text-xs font-medium leading-none text-background/40">
                      /{getPricePeriod(currentPlan.plan_id)}
                    </p>
                  )}
                </div>
              </div>

              {/* Billing cycle toggle for Content Access */}
              {isContentActive && (
                <ToggleGroup
                  value={billingCycle}
                  onValueChange={(v) => {
                    if (v) setBillingCycle(v);
                  }}
                  variant="outline"
                  type="single"
                  className="gap-0"
                >
                  <ToggleGroupItem
                    className="text-xs !h-auto px-2.5 border-r-0 !rounded-r-none border-black/10 shadow-sm py-1 data-[state=on]:bg-black data-[state=on]:text-white hover:bg-gray-100"
                    value="yearly"
                  >
                    Annual
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    className="text-xs !h-auto px-2.5 border-l-0 !rounded-l-none border-black/10 shadow-sm py-1 data-[state=on]:bg-black data-[state=on]:text-white hover:bg-gray-100"
                    value="monthly"
                  >
                    Monthly
                  </ToggleGroupItem>
                </ToggleGroup>
              )}

              {/* VC / Angel toggle for Investor plan */}
              {isVCActive && (
                <ToggleGroup
                  value={investorValue}
                  onValueChange={(v) => {
                    if (v) setInvestorValue(v);
                  }}
                  variant="outline"
                  type="single"
                  className="gap-0"
                >
                  <ToggleGroupItem
                    className="text-xs !h-auto px-2.5 border-r-0 !rounded-r-none border-black/10 shadow-sm py-1 data-[state=on]:bg-black data-[state=on]:text-white hover:bg-gray-100"
                    value="vc"
                  >
                    VC
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    className="text-xs !h-auto px-2.5 border-l-0 !rounded-l-none border-black/10 shadow-sm py-1 data-[state=on]:bg-black data-[state=on]:text-white hover:bg-gray-100"
                    value="angel"
                  >
                    Angel
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
            </div>
          </div>

          {/* CTA button — renders all PlanCards, only active one is visible */}
          <div className={`relative mt-3 ${fadeClass}`}>
            {planConfigs.map(({ plan }, i) => (
              <div
                key={i}
                className={`relative overflow-hidden ${i === activeIndex ? "block" : "hidden"}`}
              >
                <PlanCard
                  onlyButton
                  compactOnlyButton
                  description=""
                  plan={plan}
                  planSubscription={planSubscription}
                  currencyCode={currencyCode}
                  priceData={priceData}
                  priceDataMonthly={priceDataMonthly}
                  setBillingCycle={setBillingCycle}
                  billingCycle={billingCycle}
                  _priceData={_priceData}
                  highlighted={plan.plan_id === "Content Access"}
                  hasVCAngelSelection={plan.plan_id === "VC Registration"}
                  externalInvestorValue={
                    plan.plan_id === "VC Registration" ? investorValue : undefined
                  }
                />
              </div>
            ))}
          </div>

          {/* Fixed criteria list — names stay, right column fades on plan change */}
          <ul className="-my-2 text-sm leading-snug divide-y divide-background/5 mt-4">
            {allCriteria.map((_, i) => {
              const item = currentBenefits[i];
              const criteriaName = allCriteria[i].name;
              const isAllowed = item?.allowed;
              return (
                <li
                  key={i}
                  className={`flex justify-between py-2 ${
                    !isAllowed ? "bg-gray-50 shadow-inner -mx-4 px-4" : ""
                  }`}
                >
                  <span className={`text-xs ${!isAllowed ? "opacity-30" : "opacity-60"}`}>
                    {criteriaName === "Customize Watchlists and Bookmarks" ? (
                      <span className="inline-flex flex-col leading-relaxed">
                        <span className="inline-flex items-center">
                          Customize Watchlists{" "}
                          <Heart weight="bold" className="size-3 ml-1 mr-1.5" />
                        </span>
                        <span className="inline-flex items-center">
                          and Bookmarks{" "}
                          <Bookmark weight="bold" className="size-3 ml-1 mr-1.5" />
                        </span>
                      </span>
                    ) : (
                      criteriaName
                    )}
                  </span>
                  <div className={`flex items-center justify-end gap-1.5 shrink-0 ${fadeClass}`}>
                    <BenefitValue allowed={isAllowed} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MobilePricingComparison;
